import {
  BENEFIT_LABELS,
  PAYER_CLASS_OPTIONS,
  serviceLineLabel,
  tierLabel,
} from "pages/HospitalsPage/components/BenefitSelection/constants";
import {
  BenefitFallbackSource,
  IBenefitFallbackRow,
} from "types/BenefitFallbacks/BenefitFallbacks";

/**
 * CLE-3013 — the words Eligibility › Fallbacks uses for one benefit map row. Pure, so the sentences
 * are tested without rendering the table. Service lines are named the way the benefit selection
 * screen names them (`serviceLineLabel`).
 */

/** A source as a service line name. `MEDICARE` is a forced value, never a configured line. */
export const sourceLabel = (source: BenefitFallbackSource): string =>
  source === "MEDICARE"
    ? "Medicare"
    : serviceLineLabel(source as Parameters<typeof serviceLineLabel>[0]);

export const benefitLabel = (row: IBenefitFallbackRow): string =>
  BENEFIT_LABELS[row.benefit] ?? row.benefit;

const payerClassLabel = (payerClass: string): string =>
  PAYER_CLASS_OPTIONS.find((option) => option.value === payerClass)?.name ??
  payerClass;

/**
 * "Applies to", in the row's own words. A row with no scope at all is the hospital's default:
 * "Everyone".
 */
export const appliesToInWords = (row: IBenefitFallbackRow): string => {
  const parts: string[] = [];
  if (row.serviceScope === "MRI_CT") parts.push("MRI/CT estimates");
  if (row.serviceScope === "DIAGNOSTIC_MEDICAL")
    parts.push("Diagnostic Medical estimates");
  // CLE-3024.
  if (row.serviceScope === "HOSPITAL_INPATIENT")
    parts.push("Hospital Inpatient estimates");
  if (row.payerClass) parts.push(payerClassLabel(row.payerClass));
  if (row.payerIds.length > 0) {
    const named = row.payers.map((payer) => payer.name);
    const unnamed = row.payerIds.length - named.length;
    parts.push(
      [
        ...named,
        ...(unnamed > 0
          ? [`${unnamed} other payer${unnamed === 1 ? "" : "s"}`]
          : []),
      ].join(", ")
    );
  }
  if (row.planKeywords.length > 0) {
    parts.push(
      `plans containing ${row.planKeywords
        .map((keyword) => `“${keyword}”`)
        .join(" or ")}`
    );
  }
  return parts.length > 0 ? parts.join(" · ") : "Everyone";
};

/**
 * "Reads from": the service line the engine reads — the HEAD of the stored chain, which is the only
 * one it reads since the 2026-09-08 ruling (CLE-2790). The stored tail is legacy's fallback list,
 * shown separately so nobody reads it as something the engine walks.
 */
export const readsFromInWords = (
  row: IBenefitFallbackRow
): { reads: string; legacyAlso: string | null } => {
  const [head, ...tail] = row.authoritySources;
  if (!head) return { reads: "—", legacyAlso: null };
  const reads = `${sourceLabel(head.source)}${
    head.surface === "SUMMARY" ? " (summary only)" : ""
  }`;
  return {
    reads,
    legacyAlso:
      tail.length > 0
        ? tail
            .map(
              (entry) =>
                `${sourceLabel(entry.source)}${
                  entry.surface === "SUMMARY" ? " (summary only)" : ""
                }`
            )
            .join(", ")
        : null,
  };
};

const COPAYS = ["FLAT_COPAY", "PROFESSIONAL_COPAY"];

/**
 * "If missing": the ladder as a sentence, e.g. "Borrow from the Diagnostic X-Ray benefit, then
 * earlier checks of this plan, then leave blank."
 *
 * Every ladder ends blank — the engine's walk ends there whether or not the row says ABSENT — so the
 * sentence always does too. A SUBSTITUTE rung on a copay is named as never used: copays are never
 * borrowed across service lines, whatever a row says.
 */
export const ladderInWords = (row: IBenefitFallbackRow): string => {
  const absentAt = row.ladder.findIndex((rung) => rung.rung === "ABSENT");
  const walked = absentAt === -1 ? row.ladder : row.ladder.slice(0, absentAt);
  const steps = walked
    .map((rung): string | null => {
      if (rung.rung === "HISTORY") return "earlier checks of this plan";
      if (rung.rung !== "SUBSTITUTE" || COPAYS.includes(row.benefit))
        return null;
      const donor = rung.source ? sourceLabel(rung.source) : "the declared";
      return rung.source === "XRAY"
        ? `borrow from the ${donor} benefit (also when the MRI/CAT block says only “Non-Covered”)`
        : `borrow from the ${donor} benefit`;
    })
    .filter((step): step is string => step !== null);
  const sentence = [...steps, "leave blank"].join(", then ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
};

export const tierInWords = (row: IBenefitFallbackRow): string =>
  tierLabel(row.tierPreference);

/** True when the row borrows from another service line at all — the rows this tab exists for. */
export const borrows = (row: IBenefitFallbackRow): boolean =>
  !COPAYS.includes(row.benefit) &&
  row.ladder.some((rung) => rung.rung === "SUBSTITUTE");
